import { getPausedAtTs } from "@intelligems/ig-analytics";
import type { ExperienceCategory } from "@intelligems/ig-types";
import {
  type EarlyTestRuntimeInput,
  isShortRunningTest,
} from "@src/features/experiments/utils/earlyTestRuntime";
import { openDialog } from "@src/store/slices/global";

type DispatchFn = (action: ReturnType<typeof openDialog>) => unknown;

interface ReportableExperience extends EarlyTestRuntimeInput {
  id: string;
  name: string;
  type: string;
  category: ExperienceCategory;
}

/**
 * Opens the early-test report dialog when a short-running experiment was ended
 * or paused. Called from the end/pause mutation success handlers so the prompt
 * only appears after the action has succeeded. Skipped for personalizations,
 * tests that ran long enough, and ends of an already-paused test (the report was
 * already offered when it was paused).
 */
export const maybeOpenEarlyTestReport = (
  dispatch: DispatchFn,
  experience: ReportableExperience | null | undefined,
  action: "end" | "pause"
) => {
  if (!experience || experience.category !== "experiment") {
    return;
  }

  if (
    action === "end" &&
    getPausedAtTs(
      (experience.experienceActions ?? []).filter((a) => a.action !== "end")
    )
  ) {
    return;
  }

  const { daysLive, isShortRunning } = isShortRunningTest(experience);
  if (!isShortRunning) {
    return;
  }

  dispatch(
    openDialog({
      dialog: "earlyTestReport",
      props: {
        experience: {
          id: experience.id,
          name: experience.name,
          type: experience.type,
          category: experience.category,
        },
        action,
        daysLive,
      },
    })
  );
};
